import type { AmplitudeErrorReporter } from './config'

/** Script element the Guides & Surveys loader appends for its real bundle. */
export const ENGAGEMENT_SCRIPT_ID = 'engagement-sdk-bundle'

/** The loader only logs to the console when its bundle does not arrive, so we watch the element ourselves. */
const LOAD_TIMEOUT_MS = 15_000

const APPEND_TIMEOUT_MS = 30_000

interface WatchOptions {
  report: AmplitudeErrorReporter
  loadTimeoutMs?: number
  appendTimeoutMs?: number
}

const watchScript = (script: HTMLScriptElement, report: AmplitudeErrorReporter, loadTimeoutMs: number) => {
  let isSettled = false
  const timer = setTimeout(() => {
    if (isSettled) return
    isSettled = true
    report(`Guides & Surveys SDK did not load within ${loadTimeoutMs} ms`, { level: 'warn', src: script.src })
  }, loadTimeoutMs)

  script.addEventListener('load', () => {
    isSettled = true
    clearTimeout(timer)
  })
  script.addEventListener('error', () => {
    if (isSettled) return
    isSettled = true
    clearTimeout(timer)
    report('Guides & Surveys SDK script failed to load', { level: 'warn', src: script.src })
  })
}

/** Call right before `client.add(engagementPlugin)`, so the observer sees the script before it loads. */
export const watchEngagementScript = ({
  report,
  loadTimeoutMs = LOAD_TIMEOUT_MS,
  appendTimeoutMs = APPEND_TIMEOUT_MS,
}: WatchOptions): void => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return

  const existing = document.getElementById(ENGAGEMENT_SCRIPT_ID)
  if (existing instanceof HTMLScriptElement) {
    watchScript(existing, report, loadTimeoutMs)
    return
  }

  const observer = new MutationObserver(() => {
    const script = document.getElementById(ENGAGEMENT_SCRIPT_ID)
    if (!(script instanceof HTMLScriptElement)) return

    observer.disconnect()
    clearTimeout(appendTimer)
    watchScript(script, report, loadTimeoutMs)
  })

  const appendTimer = setTimeout(() => {
    observer.disconnect()
    report(`Guides & Surveys loader did not add its script within ${appendTimeoutMs} ms`, { level: 'warn' })
  }, appendTimeoutMs)

  observer.observe(document.documentElement, { childList: true, subtree: true })
}
